import { FC, useState, useCallback, useRef } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import { useLazyQuery } from "@apollo/client";
import { MENTION_ID_QUERY } from "../../../apis/taxonomy/queries";
import { TAXONOMY_CLIENT } from "../../../utils/consts";
import {
  ErrorContent,
  IdleContent,
  ProcessingContent,
  SuccessContent,
} from "./MentionUploadContent";
import { MentionNamesPayload, MentionUploadDialogProps } from "./types";
import {
  validateMentionFile,
  MentionUploadOutcome,
} from "./validateMentionFile";

type DialogState =
  | { status: "idle" }
  | { status: "processing" }
  | { status: "error"; message: string }
  | { status: "success"; result: MentionUploadOutcome };

export const MentionUploadDialog: FC<MentionUploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    status: "idle",
  });
  // Bumped on every reset so a file still being validated when the dialog is
  // cancelled or restarted cannot overwrite the state afterwards.
  const runIdRef = useRef(0);

  const [fetchMentions] = useLazyQuery<MentionNamesPayload, { ids: number[] }>(
    MENTION_ID_QUERY,
    { context: { clientName: TAXONOMY_CLIENT } }
  );

  const fetchKnownIds = useCallback(
    async (ids: number[]) => {
      const { data, error } = await fetchMentions({ variables: { ids } });
      if (error) throw new Error(`API validation failed: ${error.message}`);
      return new Set(data?.mention?.data?.map(({ id }) => id));
    },
    [fetchMentions]
  );

  const handleFilesAccepted = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      const runId = ++runIdRef.current;
      setDialogState({ status: "processing" });

      const outcome = await validateMentionFile(files[0], fetchKnownIds);
      if (runId !== runIdRef.current) return;

      setDialogState(
        outcome.error
          ? { status: "error", message: outcome.error }
          : { status: "success", result: outcome }
      );
    },
    [fetchKnownIds]
  );

  const handleReset = useCallback(() => {
    runIdRef.current += 1;
    setDialogState({ status: "idle" });
  }, []);

  const handleDialogClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleImport = useCallback(() => {
    if (dialogState.status !== "success") return;
    onSuccess(dialogState.result.validIds);
    handleDialogClose();
  }, [dialogState, onSuccess, handleDialogClose]);

  const renderContent = () => {
    switch (dialogState.status) {
      case "processing":
        return <ProcessingContent />;
      case "error":
        return <ErrorContent message={dialogState.message} />;
      case "success":
        return (
          <SuccessContent
            validCount={dialogState.result.validIds.length}
            invalidValues={dialogState.result.invalidValues}
          />
        );
      default:
        return <IdleContent onFilesAccepted={handleFilesAccepted} />;
    }
  };

  const renderActions = () => {
    switch (dialogState.status) {
      case "error":
        return (
          <>
            <Button
              onClick={handleDialogClose}
              color="secondary"
              data-testid="mentions-upload-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={handleReset}
              variant="contained"
              color="primary"
              data-testid="mentions-upload-retry-btn"
            >
              Try Again
            </Button>
          </>
        );
      case "success":
        return (
          <>
            <Button
              onClick={handleReset}
              color="secondary"
              data-testid="mentions-upload-another-btn"
            >
              Upload Another
            </Button>
            <Button
              onClick={handleImport}
              variant="contained"
              color="primary"
              data-testid="mentions-upload-import-btn"
            >
              Import
            </Button>
          </>
        );
      default:
        return (
          <Button
            onClick={handleDialogClose}
            color="secondary"
            data-testid="mentions-upload-cancel-btn"
          >
            Cancel
          </Button>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { minHeight: 350 } } }}
      data-testid="mentions-upload-dialog"
    >
      <DialogTitle sx={{ m: 0, p: 0 }}>
        <Box sx={{ p: 2 }}>
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: 600 }}
            data-testid="mentions-upload-title"
          >
            Import Mention List
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Bulk upload a list of mentions
          </Typography>
        </Box>
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
          data-testid="mentions-upload-close-btn"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>{renderContent()}</DialogContent>

      <DialogActions sx={{ p: 2 }}>{renderActions()}</DialogActions>
    </Dialog>
  );
};
